import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Send, Loader2, CheckCircle, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { shareLinksApi, type ShareLinkCreated } from '../services/api';

type Channel = 'COPY' | 'SMS' | 'EMAIL';

interface ShareLinkModalProps {
  open: boolean;
  onClose: () => void;
  // Required: which vehicle to track
  deviceId: string;
  deviceName: string;
  // Optional: tie to a mission. When provided, expiry is mission window
  // (and durationMinutes is ignored). Recipient is pre-filled.
  missionId?: string | null;
  defaultRecipientPhone?: string | null;
  defaultRecipientEmail?: string | null;
  defaultClientName?: string | null;
}

const DURATIONS = [
  { value: 5, labelKey: 'share_link.modal.duration_5' as const },
  { value: 15, labelKey: 'share_link.modal.duration_15' as const },
  { value: 30, labelKey: 'share_link.modal.duration_30' as const },
  { value: 60, labelKey: 'share_link.modal.duration_60' as const },
];

export default function ShareLinkModal({
  open,
  onClose,
  deviceId,
  deviceName,
  missionId,
  defaultRecipientPhone,
  defaultRecipientEmail,
  defaultClientName,
}: ShareLinkModalProps) {
  const { t, i18n } = useTranslation();
  const [channel, setChannel] = useState<Channel>(missionId ? (defaultRecipientEmail ? 'EMAIL' : 'SMS') : 'COPY');
  const [duration, setDuration] = useState(30);
  const [recipient, setRecipient] = useState<string>(
    missionId ? (defaultRecipientEmail || defaultRecipientPhone || '') : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ShareLinkCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleChannelChange = (next: Channel) => {
    setChannel(next);
    setError(null);
    if (next === 'EMAIL') setRecipient(defaultRecipientEmail || '');
    else if (next === 'SMS') setRecipient(defaultRecipientPhone || '');
    else setRecipient('');
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload = await shareLinksApi.create({
        deviceId,
        missionId: missionId ?? null,
        durationMinutes: missionId ? undefined : duration,
        channel,
        recipient: channel === 'COPY' ? null : recipient.trim(),
      });
      setResult(payload);
      if (payload.deliveryError) {
        setError(t('share_link.modal.delivery_failed', { error: payload.deliveryError }));
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : 'Erreur inconnue');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select-into-textarea is overkill for a 60-char URL; ignore.
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setCopied(false);
    setSubmitting(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5" />
            <h2 className="text-base font-semibold">{t('share_link.modal.title')}</h2>
          </div>
          <button onClick={handleClose} className="p-1.5 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-700">
            {t('share_link.modal.vehicle')}&nbsp;: <span className="font-medium text-gray-900">{deviceName}</span>
            {defaultClientName && (
              <>
                <br />
                {t('share_link.modal.client')}&nbsp;: <span className="font-medium text-gray-900">{defaultClientName}</span>
              </>
            )}
            {missionId && (
              <p className="text-xs text-gray-500 mt-1">
                {t('share_link.modal.mission_hint')}
              </p>
            )}
          </div>

          {!result && (
            <>
              {/* Channel selector */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">{t('share_link.modal.send_via')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['SMS', 'EMAIL', 'COPY'] as Channel[]).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => handleChannelChange(c)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                        channel === c
                          ? 'bg-cyan-50 border-cyan-600 text-cyan-700'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {c === 'COPY'
                        ? t('share_link.modal.channel_copy')
                        : c === 'SMS'
                        ? t('share_link.modal.channel_sms')
                        : t('share_link.modal.channel_email')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipient input */}
              {channel !== 'COPY' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {channel === 'SMS' ? t('share_link.modal.phone') : t('share_link.modal.email_addr')}
                  </label>
                  <input
                    type={channel === 'SMS' ? 'tel' : 'email'}
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder={channel === 'SMS' ? t('share_link.modal.phone_placeholder') : t('share_link.modal.email_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              )}

              {/* Duration selector (vehicle-only mode) */}
              {!missionId && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {t('share_link.modal.duration')}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {DURATIONS.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => setDuration(d.value)}
                        className={`px-2 py-2 rounded-lg text-sm font-medium border transition ${
                          duration === d.value
                            ? 'bg-cyan-50 border-cyan-600 text-cyan-700'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t(d.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || (channel !== 'COPY' && !recipient.trim())}
                  className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-semibold hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> {t('share_link.modal.submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" /> {channel === 'COPY' ? t('share_link.modal.submit_generate') : t('share_link.modal.submit_send')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">{t('share_link.modal.success_title')}</div>
                  <div className="text-xs text-green-700">
                    {t('share_link.modal.success_expires', { date: new Date(result.expiresAt).toLocaleString(i18n.resolvedLanguage || 'fr') })}
                    {result.delivered && result.channel !== 'COPY' && ' • ' + t('share_link.modal.success_delivered')}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('share_link.modal.link_label')}</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={result.url}
                    onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-gray-50"
                  />
                  <button
                    onClick={handleCopy}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium flex items-center gap-1.5"
                  >
                    <Copy className="w-4 h-4" />
                    {copied ? t('common.copied') : t('common.copy')}
                  </button>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-semibold hover:bg-cyan-700"
              >
                {t('common.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
